import { useContext } from "react";
import { getSessionStore } from "app/utils/sessionStore";
import { ProposalTypes } from "app/utils/types";
import PolicyHeaderIcon from "app/assets/IconComponent/PolicyHeaderIcon";
import { ThemeContext } from "app/uicomponents/macrocomponents/RootLayout";

interface PolicyOverviewData {
  shareholders: number;
  lifePolicies: number;
  disabilityPolicies: number;
  shareholderValue: string;
  lifeBenefits: string;
  disabilityBenefits: string;
}

interface PolicyOverviewCardProps {
  data?: PolicyOverviewData;
}

const PolicyOverviewCard = ({ data }: PolicyOverviewCardProps) => {
  const { isDarkMode } = useContext(ThemeContext);
  const isDark = isDarkMode;
  const isKeyPerson = getSessionStore("planInfo")?.planType === ProposalTypes.KeyPersonInsurance;

  // Key Person reuses the same shape but relabels the people / value columns.
  const COLUMNS: { key: keyof PolicyOverviewData; label: string }[] = [
    { key: "shareholders", label: isKeyPerson ? "Key Persons" : "Shareholders" },
    { key: "lifePolicies", label: "Life Policies" },
    { key: "disabilityPolicies", label: "Disability Policies" },
    { key: "shareholderValue", label: isKeyPerson ? "Exposure Value" : "Shareholder Value" },
    { key: "lifeBenefits", label: "Life Benefits" },
    { key: "disabilityBenefits", label: "Disability Benefits" },
  ];

  const headerText = isDark ? "text-[#93C5FD]" : "text-[#1E3A8A]";
  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-[#F5F5F5]";
  const thBg = isDark ? "bg-[#1E1E2E]" : "bg-white";
  const thText = isDark ? "text-gray-400" : "text-[#6A7282]";
  const tdText = isDark ? "text-gray-100" : "text-[#0F172A]";
  const divider = isDark ? "border-[#2E2E42]" : "border-[#F1F5F9]";

  return (
    <div className={`w-full rounded-2xl shadow-lg mt-4 ${cardBg}`}>
      <p
        className={`text-[13px] font-semibold conthrax-font p-4 flex items-center ${cardBg} gap-2 ${headerText}`}
      >
        <PolicyHeaderIcon fill={isDark ? "#93C5FD" : "#1E3A8A"} size={18} />
        Policy Overview
      </p>

      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="border-b-2 border-[#F5F5F5]">
            <tr className={thBg}>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-left text-[14px] font-medium uppercase whitespace-nowrap tracking-wider ${thText} border-b ${divider}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={isDark ? "" : "bg-white"}>
            {!data ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className={`px-4 py-6 text-center text-[14px] ${thText}`}
                >
                  No Data Found
                </td>
              </tr>
            ) : (
              <tr>
                {COLUMNS.map((col) => (
                  <td key={col.key} className="px-4 py-4">
                    <span className={`text-[14px] font-bold ${tdText}`}>
                      {String(data[col.key])}
                    </span>
                  </td>
                ))}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PolicyOverviewCard;
